<script setup lang="ts">
// Site chrome, top — per the mockups: wordmark + tagline on the left, an
// icon-over-label nav across the middle, and the orange "Get a Free Second
// Opinion" CTA pinned right with the service location under it.
//
// The CTA is the only orange thing in the header on purpose. Every page in
// the redesign funnels to the same action, so it should look like the same
// button every time.
//
// MOBILE (<= 860px) IS A DIFFERENT HEADER, not a squeezed version of this
// one. The desktop bar tried to fit brand + CTA + location + burger on a
// phone and wrapped to 144px — 18% of an iPhone viewport, permanently,
// because the header is sticky. Below the breakpoint the bar carries only
// the brand and the burger (~60px), and everything else moves into a
// slide-in drawer: full nav with real 52px rows, the CTA at full width,
// and the location line. Desktop markup and styles are untouched.
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { NAV_ITEMS, SECOND_OPINION_HREF, SITE_EMAIL, SITE_LOCATION } from '../nav'
// The brand mark. 128px covers the 34-38px render at 3x; the full-size
// master sits beside it as askhvac-logo.png and is deliberately NOT
// imported — bundling 1MB for a header icon would be absurd.
import logoMark from '@/assets/askhvac-logo-128.png'

const props = defineProps<{ active?: string }>()

const open = ref(false)
const items = computed(() => NAV_ITEMS)
const route = useRoute()

function isActive(id: string) {
    return props.active === id
}

// Scroll lock while the drawer is open. Without it the page scrolls
// underneath the drawer on iOS and the reader loses their place — they
// close the menu and find themselves somewhere else on the page.
watch(open, (isOpen) => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
})

// A drawer that survives navigation is a bug: the link works, the page
// changes behind the scrim, and the visitor thinks nothing happened.
watch(() => route.fullPath, () => { open.value = false })

function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') open.value = false
}

// Bound on window rather than on the drawer, so Escape works no matter
// what has focus inside it.
watch(open, (isOpen) => {
    if (isOpen) window.addEventListener('keydown', onKeydown)
    else window.removeEventListener('keydown', onKeydown)
})

// Unmounting mid-navigation would otherwise leave the body locked and the
// listener attached.
onBeforeUnmount(() => {
    document.body.style.overflow = ''
    window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
    <header class="app-header">
        <div class="bar">
            <a class="brand" href="/" aria-label="Ask-HVAC.com home">
                <img class="mark" :src="logoMark" alt="" width="128" height="128" />
                <span class="word">
                    <strong>Ask-HVAC<span class="dot">.com</span></strong>
                    <small>Get Answers Before You Call</small>
                </span>
            </a>

            <nav class="nav" :class="{ open }">
                <a
                    v-for="item in items"
                    :key="item.id"
                    class="nav-item"
                    :class="{ current: isActive(item.id) }"
                    :href="item.href"
                >
                    <span class="icon" aria-hidden="true" v-html="item.icon"></span>
                    <span class="label">{{ item.label }}</span>
                </a>
            </nav>

            <div class="right">
                <a class="cta" :href="SECOND_OPINION_HREF">
                    <span>Get a Free<br />Second Opinion</span>
                </a>
                <p class="location">
                    <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5a4.5 4.5 0 0 0-4.5 4.5c0 3.4 4.5 8.5 4.5 8.5s4.5-5.1 4.5-8.5A4.5 4.5 0 0 0 8 1.5zm0 6.2A1.7 1.7 0 1 1 8 4.3a1.7 1.7 0 0 1 0 3.4z" fill="currentColor" /></svg>
                    {{ SITE_LOCATION }}
                </p>
            </div>

            <button
                id="nav-toggle"
                class="burger"
                :class="{ open }"
                type="button"
                :aria-expanded="open"
                aria-controls="mobile-drawer"
                :aria-label="open ? 'Close menu' : 'Open menu'"
                @click="open = !open"
            >
                <span></span><span></span><span></span>
            </button>
        </div>

        <!-- Mobile drawer. Rendered at every width but display:none above
             the breakpoint, so the desktop DOM is unaffected. -->
        <div
            class="scrim"
            :class="{ show: open }"
            aria-hidden="true"
            @click="open = false"
        ></div>

        <nav
            id="mobile-drawer"
            class="drawer"
            :class="{ open }"
            aria-label="Site menu"
            :inert="!open || undefined"
        >
            <div class="drawer-head">
                <span class="drawer-title">Menu</span>
                <button class="drawer-close" type="button" aria-label="Close menu" @click="open = false">
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" />
                    </svg>
                </button>
            </div>

            <ul class="drawer-nav">
                <li v-for="item in items" :key="item.id">
                    <a
                        class="drawer-link"
                        :class="{ current: isActive(item.id) }"
                        :href="item.href"
                        :aria-current="isActive(item.id) ? 'page' : undefined"
                    >
                        <span class="d-icon" aria-hidden="true" v-html="item.icon"></span>
                        <span class="d-label">{{ item.label }}</span>
                        <span class="chev" aria-hidden="true">›</span>
                    </a>
                </li>
            </ul>

            <div class="drawer-foot">
                <a class="drawer-cta" :href="SECOND_OPINION_HREF">Get a Free Second Opinion →</a>
                <p class="drawer-loc">
                    <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5a4.5 4.5 0 0 0-4.5 4.5c0 3.4 4.5 8.5 4.5 8.5s4.5-5.1 4.5-8.5A4.5 4.5 0 0 0 8 1.5zm0 6.2A1.7 1.7 0 1 1 8 4.3a1.7 1.7 0 0 1 0 3.4z" fill="currentColor" /></svg>
                    {{ SITE_LOCATION }}
                </p>
                <a class="drawer-mail" :href="`mailto:${SITE_EMAIL}`">{{ SITE_EMAIL }}</a>
            </div>
        </nav>
    </header>
</template>

<style scoped lang="scss">
.app-header {
    position: sticky;
    top: 0;
    z-index: 30;
    background: var(--site-surface);
    border-bottom: 1px solid var(--site-border);
    box-shadow: var(--site-shadow-sm);
}

.bar {
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: 0.6rem var(--site-space-lg);
    display: flex;
    align-items: center;
    gap: var(--site-space-lg);
}

.brand {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    text-decoration: none;
    color: var(--site-navy);
    flex-shrink: 0;

    .mark {
        width: 2.4rem;
        height: 2.4rem;
        /* The artwork carries its own navy field, so it sits on the white
           header as a tile. Rounding it stops that reading as a stray
           screenshot. */
        border-radius: 7px;
        display: block;
    }

    .word {
        display: flex;
        flex-direction: column;
        line-height: 1.1;
    }

    strong {
        font-family: var(--site-font-heading);
        font-size: 1.25rem;
        font-weight: 800;
        letter-spacing: -0.03em;
    }

    .dot {
        color: var(--site-blue);
    }

    small {
        font-size: 0.62rem;
        font-weight: 600;
        letter-spacing: 0.02em;
        color: var(--site-text-muted);
    }
}

.nav {
    display: flex;
    align-items: flex-end;
    gap: 0.15rem;
    margin-left: auto;
}

.nav-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.15rem;
    padding: 0.35rem 0.6rem 0.3rem;
    text-decoration: none;
    color: var(--site-navy);
    border-bottom: 2px solid transparent;
    white-space: nowrap;

    .icon :deep(svg) {
        width: 1.05rem;
        height: 1.05rem;
        display: block;
    }

    .label {
        font-size: 0.72rem;
        font-weight: 600;
    }

    &:hover {
        color: var(--site-blue);
    }

    &.current {
        color: var(--site-blue);
        border-bottom-color: var(--site-accent);
    }
}

.right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.2rem;
    flex-shrink: 0;
}

.cta {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.42rem 0.85rem;
    border-radius: var(--site-radius-pill);
    background: var(--site-gradient-cta);
    color: var(--site-accent-contrast);
    text-decoration: none;
    font-family: var(--site-font-body);
    font-size: 0.72rem;
    font-weight: 700;
    line-height: 1.15;
    box-shadow: var(--site-shadow-sm);

    &:hover {
        background: var(--site-accent-strong);
    }
}

.location {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    margin: 0;
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--site-text-muted);

    svg {
        width: 0.75rem;
        height: 0.75rem;
        color: var(--site-accent);
    }
}

/* 44x44 is the smallest reliably tappable target (Apple HIG). The bars
   inside stay 22px wide — the hit area is padding, not paint. */
.burger {
    display: none;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 5px;
    width: 44px;
    height: 44px;
    margin-right: -0.35rem;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;

    span {
        display: block;
        width: 22px;
        height: 2px;
        background: var(--site-navy);
        border-radius: 2px;
        transition: transform 0.22s ease, opacity 0.16s ease;
    }

    /* Bars fold into an X, so the control reads as "close" once open. */
    &.open span:nth-child(1) {
        transform: translateY(7px) rotate(45deg);
    }

    &.open span:nth-child(2) {
        opacity: 0;
    }

    &.open span:nth-child(3) {
        transform: translateY(-7px) rotate(-45deg);
    }
}

@media (max-width: 1080px) {
    .nav-item .label {
        display: none;
    }

    .nav-item {
        padding: 0.4rem 0.5rem;
    }
}

/* ---------------------------------------------------------------- mobile
   Below this width the header is brand + burger and nothing else. The
   desktop nav, CTA and location line are hidden here and re-presented
   inside the drawer, where they get room to be tappable. */
@media (max-width: 860px) {
    .bar {
        padding: 0.5rem var(--site-space-md);
        gap: var(--site-space-sm);
        justify-content: space-between;
    }

    .nav,
    .right {
        display: none;
    }

    .burger {
        display: flex;
        margin-left: auto;
    }

    .brand .mark {
        width: 2.1rem;
        height: 2.1rem;
    }

    .brand strong {
        font-size: 1.12rem;
    }

    /* 9.9px on a phone is decoration, not a line anyone reads. */
    .brand small {
        font-size: 0.72rem;
    }

    /* The logo is the "go home" affordance and was a 34px target. */
    .brand {
        min-height: 44px;
    }
}

/* The tagline is the first thing to go when width runs short — the
   wordmark alone still identifies the site. Held down to 340px so the
   common phone widths (360, 375, 390, 393) all keep the positioning line;
   it costs ~6px of bar height, which is worth it. */
@media (max-width: 340px) {
    .brand small {
        display: none;
    }
}

/* ---------------------------------------------------------------- drawer */
.scrim,
.drawer {
    display: none;
}

@media (max-width: 860px) {
    .scrim {
        display: block;
        position: fixed;
        inset: 0;
        z-index: 40;
        background: rgba(7, 28, 69, 0.55);
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.24s ease;
    }

    .scrim.show {
        opacity: 1;
        pointer-events: auto;
    }

    .drawer {
        display: flex;
        flex-direction: column;
        position: fixed;
        top: 0;
        right: 0;
        z-index: 41;
        /* Leaves a strip of the page visible so the drawer reads as a
           layer over the site rather than a new screen — and gives the
           scrim a comfortable tap area for dismissing it. */
        width: min(78vw, 300px);
        height: 100dvh;
        background: var(--site-surface);
        box-shadow: -8px 0 28px rgba(7, 28, 69, 0.22);
        transform: translateX(100%);
        transition: transform 0.26s cubic-bezier(0.32, 0.72, 0, 1);
        overflow-y: auto;
        overscroll-behavior: contain;
        /* Notch and home-indicator safe areas. */
        padding-bottom: env(safe-area-inset-bottom);
    }

    .drawer.open {
        transform: translateX(0);
    }
}

.drawer-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 0.5rem 0.75rem 1.1rem;
    border-bottom: 1px solid var(--site-border);
}

.drawer-title {
    font-family: var(--site-font-heading);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--site-text-muted);
}

.drawer-close {
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--site-navy);

    svg {
        width: 1.35rem;
        height: 1.35rem;
        fill: none;
    }
}

.drawer-nav {
    list-style: none;
    margin: 0;
    padding: 0.35rem 0;
    flex: 1 1 auto;
}

.drawer-link {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    /* 52px rows: comfortably above the 44px floor, and big enough that a
       thumb reaching across the screen doesn't need aiming. */
    min-height: 52px;
    padding: 0 1.1rem;
    text-decoration: none;
    color: var(--site-navy);
    border-left: 3px solid transparent;

    .d-icon :deep(svg) {
        display: block;
        width: 1.15rem;
        height: 1.15rem;
        color: var(--site-blue);
    }

    .d-label {
        flex: 1 1 auto;
        font-size: 0.98rem;
        font-weight: 600;
    }

    .chev {
        font-size: 1.15rem;
        color: var(--site-border);
    }

    &:active {
        background: var(--site-bg-alt);
    }

    &.current {
        background: var(--site-blue-soft);
        border-left-color: var(--site-accent);

        .d-label {
            color: var(--site-blue);
        }
    }
}

.drawer-foot {
    padding: 1rem 1.1rem 1.4rem;
    border-top: 1px solid var(--site-border);
    background: var(--site-bg-alt);
}

.drawer-cta {
    display: block;
    min-height: 48px;
    line-height: 48px;
    padding: 0 1rem;
    border-radius: var(--site-radius-sm);
    background: var(--site-gradient-cta);
    color: #fff;
    font-weight: 700;
    font-size: 0.92rem;
    text-align: center;
    text-decoration: none;
}

.drawer-loc {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    margin: 0.85rem 0 0.35rem;
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--site-text-muted);

    svg {
        width: 0.85rem;
        height: 0.85rem;
        color: var(--site-accent);
    }
}

.drawer-mail {
    font-size: 0.8rem;
    color: var(--site-blue);
    text-decoration: none;
}

/* Vestibular safety: the slide becomes a fade for anyone who asks for
   reduced motion. */
@media (prefers-reduced-motion: reduce) {
    .drawer,
    .scrim,
    .burger span {
        transition-duration: 0.01ms;
    }
}
</style>
